import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import api from '@/lib/axios';
import { toast } from 'sonner';
import { ArrowLeft, Copy, ExternalLink, Video } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { attachJitsiLeaveHandler, jitsiConfigOverwrite, jitsiInterfaceConfigOverwrite, loadJitsiApi } from '@/lib/jitsi';
import { formatClocks } from '@/lib/utils';

export default function OnlineLessonRoom() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [ended, setEnded] = useState(false);
  const jitsiRef = useRef(null);

  const coursePath = `/courses/${courseId}`;

  const leaveToCourse = () => {
    try { jitsiRef.current?.dispose(); } catch { /* noop */ }
    jitsiRef.current = null;
    navigate(coursePath, { replace: true });
  };

  useEffect(() => {
    api.get(`/online-lessons/courses/${courseId}/join`)
      .then((r) => {
        setInfo(r.data);
        if (r.data.notifying_participants) {
          toast.info('Teilnehmer werden benachrichtigt (App-Push + Portal-Nachricht)');
        }
      })
      .catch((e) => setError(e.response?.data?.detail || 'Online-Raum konnte nicht geladen werden'));
  }, [courseId]);

  useEffect(() => {
    if (!info || ended) return undefined;
    let disposed = false;

    const init = () => {
      if (disposed || !window.JitsiMeetExternalAPI) return;
      const container = document.getElementById('jitsi-container');
      if (!container) return;
      const j = new window.JitsiMeetExternalAPI(info.domain, {
        roomName: info.room,
        parentNode: container,
        width: '100%',
        height: '100%',
        configOverwrite: jitsiConfigOverwrite({
          prejoinPageEnabled: true,
          subject: info.subject,
          toolbarButtons: ['microphone', 'camera', 'desktop', 'chat', 'tileview', 'settings', 'hangup'],
        }),
        interfaceConfigOverwrite: jitsiInterfaceConfigOverwrite(),
        userInfo: { displayName: info.display_name },
      });
      j.addEventListener('videoConferenceJoined', () => {
        j.executeCommand('displayName', info.display_name);
        j.executeCommand('subject', info.subject);
      });
      attachJitsiLeaveHandler(j, () => {
        jitsiRef.current = null;
        setEnded(true);
      });
      jitsiRef.current = j;
    };

    loadJitsiApi(info.domain).then(init).catch(() => {
      setError('Jitsi-Skript konnte nicht geladen werden');
    });
    return () => {
      disposed = true;
      if (jitsiRef.current) { try { jitsiRef.current.dispose(); } catch { /* noop */ } jitsiRef.current = null; }
    };
  }, [info, ended]);

  const copyLink = () => {
    navigator.clipboard.writeText(info.url)
      .then(() => toast.success('Teilnahme-Link kopiert — kann an Eltern/Schüler weitergegeben werden'))
      .catch(() => toast.error('Kopieren fehlgeschlagen'));
  };

  return (
    <Layout>
      <div className="p-4 h-[calc(100vh-100px)] flex flex-col" data-testid="online-lesson-room">
        <div className="flex items-center gap-3 mb-3">
          <Button variant="outline" size="sm" onClick={leaveToCourse} data-testid="online-room-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Zurück
          </Button>
          <Video className="w-5 h-5 text-brand-red" />
          <div>
            <div className="text-sm font-semibold text-slate-800">{formatClocks(info?.course_name) || info?.course_name || 'Onlineunterricht'}</div>
            <div className="text-[11px] text-slate-500">Video-Meeting (Jitsi) — Raum nur über diesen unratbaren Link erreichbar</div>
          </div>
          {info && (
            <Button variant="outline" size="sm" className="ml-auto" onClick={copyLink} data-testid="online-room-copy-link-btn">
              <Copy className="w-4 h-4 mr-1" /> Teilnahme-Link kopieren
            </Button>
          )}
        </div>
        {error ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 text-sm text-red-600 bg-white border border-slate-200 rounded-lg px-6" data-testid="online-room-error">
            <p>{error}</p>
            {info?.url && (
              <Button
                variant="outline"
                onClick={() => window.open(info.url, '_blank', 'noopener,noreferrer')}
                data-testid="online-room-open-external"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Meeting im neuen Tab öffnen
              </Button>
            )}
          </div>
        ) : ended ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 bg-white border border-slate-200 rounded-lg px-6" data-testid="online-room-ended">
            <Video className="w-8 h-8 text-slate-300" />
            <p className="text-sm font-medium text-slate-800">Unterricht beendet</p>
            <p className="text-xs text-slate-500 text-center max-w-sm">
              Kamera und Mikrofon sind aus. Du kannst zum Kurs zurückkehren.
            </p>
            <Button onClick={leaveToCourse} className="bg-brand-red text-white hover:bg-brand-red/90" data-testid="online-room-back-to-course">
              <ArrowLeft className="w-4 h-4 mr-1" /> Zurück zum Kurs
            </Button>
          </div>
        ) : (
          <div id="jitsi-container" className="flex-1 bg-slate-900 rounded-lg overflow-hidden" data-testid="jitsi-container">
            {!info && <div className="h-full flex items-center justify-center text-sm text-slate-400">Lade Online-Raum…</div>}
          </div>
        )}
      </div>
    </Layout>
  );
}
